import React, { useState } from 'react'
import Loader from '../Layout/Loader'
import mainAxios from '../../services/mainAxios'
import { usePersistedState } from '../../AppContext'
import useStateWithCallback from '../../services/useStateWithCallback'
import { useTranslation } from "react-i18next";
import PrimeTable from '../PrimeTable/PrimeTable'
import DateRange from '../global/DateRange'
import ExportCsvPrime from '../global/ExportCsvPrime'
import { defaultTableRows } from '../../services/data'
import { CellTextarea } from '../global/CellInput'
import ReactSelectPagination from '../global/ReactSelectPagination'
import { customStyles } from '../../design/selectStyle'

const moment = require("moment")

const AuthenticationLogs = () => {
    const { t } = useTranslation('translations');
    const [logsForCSV, setLogsForCSV] = useState([]);

    const defaultFilters = {
        fromDate: new Date(),
        toDate: new Date(),
        params: {}
    };
    const [filters, setFilters] = React.useState(defaultFilters);

    const [persistedRowPerPage, setPersistedRowPerPage] = usePersistedState(
        10,
        "rowsPerPage"
    );

    const defaultSortCriteria = {
        field: "recordDate",
        direction: "DESC"
    }

    const [tableRows, setTableRows] = useState({
        ...defaultTableRows,
        rowsPerPage: persistedRowPerPage,
        sortCriteria: defaultSortCriteria
    });
    const [searchQuery, setSearchQuery] = useState("");

    const [state, setState] = useStateWithCallback({
        logs: null,
    })
    const [loading, setLoading] = useState(false);

    const [companies, setCompanies] = useState([])

    const [companiesPagination, setCompaniesPagination] = useState({
        totalRows: 0,
        pageSize: 50,
        pageNumber: 1,
    })

    React.useEffect(() => {
        getFilteredResults({})
    }, [])

    const getFilteredResults = ({
        pageSize = persistedRowPerPage ?? defaultTableRows.rowsPerPage,
        page = defaultTableRows.currentPage,
        sortCriteria = [defaultSortCriteria],
        getLogsForCsv = false,
        updatedFilters = filters,
        fromDate = defaultFilters.fromDate,
        toDate = defaultFilters.toDate
    } = {}) => {

        setLoading(true);
        if (logsForCSV.length > 0) setLogsForCSV([]);
        return mainAxios.post("apiEndpoint/search", {
            object: "GetAuthenticationLogs",
            value: null,
            fromDate,
            toDate,
            params: {
                ...updatedFilters.params
            },
            pagination: getLogsForCsv ? null : {
                pageSize,
                pageNumber: page,
            },
            sortCriteria,
        }).then((res) => {
            setLoading(false);
            if (res.data.status) {
                if (getLogsForCsv) {
                    setLogsForCSV(res.data.content);
                } else {
                    setTableRows((tableRows) => ({
                        ...tableRows,
                        totalRows: res.data.totalSize,
                    }));
                    setState((state) => ({ ...state, logs: res.data.content }));
                }
                return true;
            } else {
                setTableRows(defaultTableRows);
                setState((state) => ({ ...state, logs: [] }));
            }

            return false;
        }).catch(error => {
            setLoading(false);
            setState((state) => ({ ...state, logs: state.logs ?? [] }));
            return false;
        });
    };

    const getCompaniesResults = ({ pageSize = 50, pageNumber = 1, searchQuery, loadMore } = {}) => {
        return mainAxios.post('apiEndpoint/search', {
            object: "GetSimpleCompany",
            value: null,
            companyFilterParams: {
                companyLiveSearch: searchQuery,
            },
            pagination: {
                pageSize,
                pageNumber,
            },
            sortCriteria: [{ field: "name", direction: "ASC" }],
        }).then(res => {
            if (res?.data?.status === true) {
                const companies = res.data.content
                setCompaniesPagination({
                    pageNumber,
                    pageSize,
                    totalRows: res?.data?.totalSize,
                })
                setCompanies((currentCompanies) => loadMore
                    ? (currentCompanies ?? []).concat(companies)
                    : companies)
                return companies
            }
            return []
        }).catch(err => {
            return []
        })
    }

    const handleCompanyChange = (value) => {
        const { company, ...params } = filters.params ?? {}
        const updatedFilters = {
            ...filters,
            params: value?.id ? { ...params, company: value.id.toString() } : params
        }
        setFilters(updatedFilters)
        getFilteredResults({ updatedFilters, fromDate: filters.fromDate, toDate: filters.toDate })
        if (value === null) {
            getCompaniesResults({ pageNumber: 1, pageSize: 50, searchQuery: "" })
        }
    }

    React.useEffect(() => {
        getCompaniesResults({ pageNumber: 1, pageSize: 50 })
    }, [])

    const companiesOptions = companies ?? []
    const companyFilterElement = () => (
        <div style={{ minWidth: 180 }}>
            <ReactSelectPagination
                styles={customStyles}
                value={companiesOptions.find(company => company.id == filters?.params?.company) ?? null}
                options={companiesOptions}
                onChange={handleCompanyChange}
                placeholder={t('customers.company')}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                getFilteredResults={getCompaniesResults}
                pagination={companiesPagination}
                setPagination={setCompaniesPagination}
                isClearable={true}
                menuPortalTarget={document.body}
            />
        </div>
    )

    const type = [
        { value: "error", name: "Error" },
        { value: "success", name: "Success" },
    ]

    const columns = [
        {
            header: t(['logs.user']),
            field: 'recordUser',
            default: true,
            mobile: { priority: 1, isTitle: true, group: "identity" },
            filter: true,
            body: log => log.recordUser ? log.recordUser : "",
            style: { minWidth: "160px", maxWidth: "160px" },
        },
        {
            header: t(['subscription.type']),
            field: 'type',
            default: true,
            mobile: { priority: 1, isBadge: true, group: "status" },
            style: { minWidth: "150px", maxWidth: "150px" },
            filterType: "DROPDOWN",
            filterConfig: {
                options: type,
                optionLabel: "name",
                optionValue: "value",
            },
            filter: true,
        },
        {
            header: t(['home.date']),
            field: 'recordDate',
            default: true,
            mobile: { priority: 1, group: "dates" },
            formattedExportValue: true,
            preventExcelFormat: true,
            filterType: "DATE",
            filterConfig: {
                field: "recordDate"
            },
            body: log => log.recordDate ? moment(log.recordDate).format("DD/MM/YYYY HH:mm") : "",
            style: { minWidth: "190px", maxWidth: "190px" },
            filter: true,
            sortable: false
        },
        {
            header: t(['customers.company']),
            field: 'company',
            default: true,
            mobile: { priority: 1, group: "identity" },
            filter: true,
            filterElement: companyFilterElement,
            sortable: true,
            body: log => log.company ? log.company : "",
            style: { minWidth: "220px", maxWidth: "220px" },
        },
        {
            header: t(['common.source']),
            field: 'source',
            default: true,
            mobile: { priority: 1, group: "details" },
            filter: true,
            body: log => log.source ? log.source : "",
            style: { minWidth: "150px", maxWidth: "150px" },
        },
        {
            header: t(['table.errorCode']),
            field: 'errorCode',
            default: false,
            filter: true,
            body: log => log.errorCode ? log.errorCode : "",
            style: { minWidth: "140px", maxWidth: "140px" },
        },
        {
            header: 'Info',
            field: 'logInfo',
            default: true,
            mobile: { priority: 1, group: "details" },
            filter: true,
            body: (log) => <CellTextarea width={250} value={log?.logInfo ?? ""} onFocus={(event) => event.target.select()} />,
        }
    ];

    const [selectedColumns, setSelectedColumns] = usePersistedState(
        columns.map((col) => ({ field: col.field, show: !!col.default })),
        "authenticationLogsColumns"
    );

    const headerIcons = (
        <div className='d-flex align-items-center gap-2'>
            <DateRange
                size="sm"
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                onChange={(dates) => {
                    const [fromDate, toDate] = dates;
                    if (fromDate && toDate) {
                        getFilteredResults({ fromDate, toDate });
                    }
                    setFilters((filters) => ({
                        ...filters,
                        fromDate,
                        toDate,
                    }));
                }}
            />
            <ExportCsvPrime
                selectedColumns={selectedColumns}
                iconOnly
                tooltip="Export CSV"
                data={logsForCSV}
                columns={columns}
                onClick={() => getFilteredResults({ getLogsForCsv: true, fromDate: filters.fromDate, toDate: filters.toDate })}
            />
        </div>
    )

    return (
        state.logs ?
            <PrimeTable
                loading={loading}
                columns={columns}
                value={[...state.logs]}
                showFilters={true}
                globalFilterValue={[searchQuery, setSearchQuery]}
                tableRows={[tableRows, setTableRows]}
                clearFilters={true}
                showSelectColumns
                gridKey="authenticationLogsColumns"
                selectedColumns={[selectedColumns, setSelectedColumns]}
                reorderableColumns={true}
                persistedRowPerPage={[persistedRowPerPage, setPersistedRowPerPage]}
                filters={[filters, setFilters]}
                defaultFilters={defaultFilters}
                defaultSortCriteria={defaultSortCriteria}
                headerIcons={headerIcons}
                columnDeps={[filters?.params?.company, companies, companiesPagination]}
                onRemoteDataChange={(remoteData) => {
                    const updatedFilters = remoteData.filters ?? filters;
                    const updatedTableData = remoteData.tableRows ?? tableRows;
                    let { currentPage, rowsPerPage, sortCriteria } = updatedTableData;
                    sortCriteria = sortCriteria ? [sortCriteria] : [];
                    getFilteredResults({
                        updatedFilters,
                        pageSize: rowsPerPage,
                        page: currentPage,
                        sortCriteria,
                        fromDate: updatedFilters.fromDate,
                        toDate: updatedFilters.toDate
                    });
                }}
            />
            :
            <Loader />
    )
}

export default AuthenticationLogs
